import type {GrowthBook} from '@growthbook/growthbook'
import {get} from './apiClient'
import {GROWTHBOOK_API_HOST as DEFAULT_GROWTHBOOK_API_HOST} from '@/config/envConfig'

/**
 * GrowthBook Feature Flags Service
 * Uses official GrowthBook SDK with Okta authentication
 */

// Feature flag map returned from GrowthBook
export type FeatureFlags = Record<string, unknown>

/**
 * Get the GrowthBook API base URL based on environment
 */
export const getGrowthBookApiUrl = (): string => {
  return DEFAULT_GROWTHBOOK_API_HOST
}

// Interface for GrowthBook API params
export interface GrowthBookFeaturesParams {
  apiHost: string
  clientKey: string
  forceRefresh?: boolean
}

/**
 * Fetches feature flags from GrowthBook API
 * Uses shared apiClient with Okta authentication via interceptors
 * @param params - Object containing apiHost and clientKey
 * @returns Promise with features data
 */
export const fetchFeaturesWithOkta = async (params: GrowthBookFeaturesParams): Promise<any> => {
  try {
    const {apiHost, clientKey, forceRefresh = false} = params
    const url = `${apiHost}/features/${clientKey}`

    // Use shared get() method which includes Okta auth via interceptors
    const response = await get(
      url,
      forceRefresh
        ? {
            headers: {'x-no-cache': 'true'},
            params: {timestamp: Date.now()},
          }
        : undefined,
    )
    return response.data
  } catch (error) {
    console.error('Error fetching features from GrowthBook:', error)
    throw error
  }
}

/**
 * Extract typed feature flags from GrowthBook instance
 * @param gb - GrowthBook instance
 * @returns Typed feature flags object
 */
export const getFeatureFlags = (gb: GrowthBook): FeatureFlags => {
  const featureKeys = Object.keys(gb.getFeatures())
  return featureKeys.reduce<FeatureFlags>((acc, key) => {
    const result = gb.evalFeature(key)
    acc[key] = result.value
    return acc
  }, {})
}

export const growthbookService = {
  getFeatureFlags,
  fetchFeaturesWithOkta,
}

export default growthbookService
